import { TicketStatesReceiving, TicketRequestType } from '../../../components/ticket/ticket.constants';
import { BaseController } from '../../base/controller';
import _ from 'lodash';

const CUSTOM_REASON = 'CUSTOM';

export class ConfirmAuthorizationController extends BaseController {
  constructor(
    controllerService,
    ticketService,
    $scope,
    fileResponseService,
    fdFormFieldsService,
    acceptanceReasons
  ) {
    'ngInject';
    super({
      controllerService,
      ticketService,
      $scope,
      fileResponseService,
      fdFormFieldsService,
      acceptanceReasons
    });
  }

  get ticketId() { return this.ticket.id; }

  get form() { return this.ticketAuthorizationForm; }

  set form(value) { this.ticketAuthorizationForm = value; }

  get customReason() {
    return CUSTOM_REASON;
  }

  $onInit() {
    // Assign
    this.model = {};

    const { nonAcceptanceReasons } = this.ticket.ticket;
    this.nonAcceptanceReasons = this.parseAcceptanceReasons(nonAcceptanceReasons);

    // Trigger
    // this.watchReasonChanges();
    this.submitDisabled = false;
    this.rejectDisabled = false;

    // Show modal for uploading warrant file
    this.uploadModalVisible = this.showUploadModal();

    // Set initial warrant pdf url and register for ticket changes
    // (in case of a newly uploaded warrant file).
    this.updateWarrantUrl();
    this.ticketService.registerForChange(this.ticketChanged.bind(this));
  }

  updateWarrantUrl() {
    // Only assign signedWarrantUrl if the ticket has the signedWarrant status
    if (this.warrantExists()) {
      this.signedWarrantUrl = this.ticketService.getSignedWarrantUrl(this.ticketId);
    }
  }

  showUploadModal() {
    return this.ticketInitializedByUser() && !this.warrantExists();
  }

  ticketInitializedByUser() {
    return this.ticketService.hasStatus(
      this.ticket, TicketStatesReceiving.INITIALIZATION_CONFIRMED_BY_USER
    );
  }

  warrantExists() {
    const ticketType = TicketRequestType[this.ticket.requestType] || this.ticket.requestType;
    // the verification, if an warrant exists, should only take place if it is an incoming ticket
    if(ticketType !== TicketRequestType.Incoming) {
      return true;
    }

    const signedWarrantExists = this.ticketService.statusHistoryContains(
      this.ticket, TicketStatesReceiving.WARRANT_SIGNED
    );

    const receivedSignedWarrantUploaded = this.ticketService.statusHistoryContains(
      this.ticket, TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED
    );

    return signedWarrantExists || receivedSignedWarrantUploaded;
  }

  ticketChanged(ticket) {
    this.ticket = ticket;
    this.updateWarrantUrl();
  }

  parseAcceptanceReasons(reasons) {
    return reasons;
  }

  checkAcceptanceReasons() {
    return _.every(this.nonAcceptanceReasons, (reason) => {
      // Check all non-custom reasons for truthy value
      if (reason !== this.customReason) {
        return this.model[reason] === true;
      }

      // If the custom reason is checked, its statement is required as well
      if (this.model[this.customReason] === true) {
        return !!this.model.customReason;
      }

      // Check if there are any non acceptance reasons or only 'CUSTOM' rejection.
      // If the latter, ensure that custom is set, not undefined.
      if (reason === this.customReason && this.nonAcceptanceReasons.length === 1) {
        return angular.isDefined(this.model[this.customReason]);
      }

      return true;
    });
  }

  someReasonsFalsy() {
    return _.some(this.nonAcceptanceReasons, (reason) => {
      // Check all non-custom reasons for falsy value
      if (reason !== this.customReason) {
        return this.model[reason] === false;
      }

      // For the custom reason its statement is required
      if (this.model[this.customReason] === true) {
        return !!this.model.customReason;
      }
    });
  }

  watchReasonChanges() {
    // Watch changes of reasons
    this.$scope.$watchCollection('vm.model', () => {
      // As soon as at least one of the reasons is not truthy, disable the submit button
      this.submitDisabled = !this.checkAcceptanceReasons();

      // As soon as any reason is falsy (except custom). enable the reject button
      this.rejectDisabled = !this.someReasonsFalsy();
    });
  }

  toggleResizeable() {
    this.isLayoutResizeable = !this.isLayoutResizeable;
  }

  getAcceptanceReasonClass(reason) {
    // Ignore custom reason
    if (reason === this.customReason) {
      return;
    }

    if (this.model[reason] === true) {
      return 'success';
    }
    else if (this.model[reason] === false) {
      return 'danger';
    }
  }

  getReasons() {
    return _.reduce(this.model, (result, value, key) => {
      // Exclude truthy reasons and 'customReason' field (will be attached to CUSTOM)
      if ((value === true && key !== this.customReason) || (key === 'customReason')) {
        return result;
      }

      const reason = { reason: key };

      // Add statement to custom reason
      if (key === this.customReason) {
        reason.statement = this.model.customReason;
      }

      result.push(reason);
      return result;
    }, []);
  }

  checkAllReasons() {
    this.nonAcceptanceReasons.map(reason => {
      // Skip custom reason checkbox
      if (reason === this.customReason) {
        return;
      }
      this.model[reason] = true;
    });
  }

  submitAuthorization() {
    // All reasons has to be approved in order to submit the authorization
    const accepted = this.checkAcceptanceReasons();
    if (!accepted) {
      this.submitAuthorizationFailed = true;
      return;
    }

    this.submitAuthorizationFailed = false;
    this.fdFormFieldsService
      .validate(this.ticketAuthorizationForm)
      .busy(this, 'submitBusy')
      .then(() => this.submit().busy(this, 'submitBusy'))
      .catch(() => this.showFormError = true);
  }

  rejectAuthorization() {
    // At least one reason has to be rejected in order to reject the authorization
    const someReasonsFalsy = this.someReasonsFalsy();
    if (!someReasonsFalsy) {
      this.rejectAuthorizationFailed = true;
      return;
    }

    const reasons = this.getReasons();
    this.rejectAuthorizationFailed = false;

    return this.reject({ reasons }).busy(this, 'rejectBusy');
  }

  uploadSignedWarrant(file) {
    return this.ticketService
      .uploadSignedWarrant(file, this.ticket.id)
      .busy(this, 'isUploadBusy')
      .then((resp) => {
        this.uploaded = true;
        return resp;
      })
      .catch((err) => this.uploaded = false);
  }

  /**
   * When upload of signed warrant was successful, close upload-modal and rerender pdf-viewer with
   * new document.
   */
  handleConfirmUpload() {
    // For direct bank flow we dont need to authorizate the signed warrant
    // at this point for warrants initialized by user.
    if (this.ticketInitializedByUser() && this.config.isDirectBank) {
      const { ticket, ticketService } = this;
      return ticketService.initializationConfirmation(ticket.id);
    }

    // Close modal if file was uploaded
    if (this.signedWarrantUrl || this.uploaded) {
      this.uploadModalVisible = false;
      this.updateWarrantUrl();
    }

    // TODO error handling
  }
}
